import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  MenuItem,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Divider,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Chip,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Person as PersonIcon,
  ShoppingCart as CartIcon,
  Receipt as ReceiptIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { orderApi, customerApi, stitchItemApi, measurementApi, walletApi } from '../services/api';
import { 
  OrderFormData, 
  OrderItemFormData, 
  MeasurementFormData,
  OrderApiData,
  Customer, 
  StitchItem, 
  Measurement,
  ApiResponse,
  PaginationParams,
  PaginationInfo,
  WalletBalance
} from '../types';
import { ORDER_STATUS, DEFAULT_MEASUREMENTS, APP_CONFIG } from '../utils/constants';

const steps = ['Customer Selection', 'Order Items', 'Measurements & Pricing', 'Review & Submit'];

const NewOrder: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Data
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [stitchItems, setStitchItems] = useState<StitchItem[]>([]);
  const [walletBalance, setWalletBalance] = useState<WalletBalance | null>(null);
  
  // Form data
  const [formData, setFormData] = useState<OrderFormData>({
    customer_id: 0,
    order_date: new Date().toISOString().split('T')[0], // Current date in YYYY-MM-DD format
    delivery_date: '',
    advance_amount: '0',
    notes: '',
    items: [],
  });
  
  // UI state
  const [showCustomerDialog, setShowCustomerDialog] = useState(false);
  const [showItemDialog, setShowItemDialog] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Customer search state
  const [customerSearchLoading, setCustomerSearchLoading] = useState(false);
  const [customerPagination, setCustomerPagination] = useState<PaginationInfo>({
    current_page: 1,
    per_page: 10,
    total_records: 0,
    total_pages: 0,
    has_next: false,
    has_prev: false
  });
  const [customerSearchParams, setCustomerSearchParams] = useState<PaginationParams>({
    page: 1,
    limit: 10,
    search: '',
    sort_by: 'name',
    sort_order: 'ASC'
  });

  useEffect(() => {
    loadInitialData();
  }, []);

  // Handle pre-selected customer from navigation state
  useEffect(() => {
    if (location.state?.selectedCustomer) {
      const customer = location.state.selectedCustomer;
      setSelectedCustomer(customer);
      setFormData(prev => ({
        ...prev,
        customer_id: customer.id
      }));
      // Add the pre-selected customer to the customers array so it's available in the dialog
      setCustomers(prev => {
        const exists = prev.find(c => c.id === customer.id);
        return exists ? prev : [...prev, customer];
      });
    }
  }, [location.state]);

  // Load customers when dialog opens
  useEffect(() => {
    if (showCustomerDialog) {
      loadCustomers();
    }
  }, [showCustomerDialog]);

  const refreshWalletBalance = async () => {
    try {
      const walletRes = await walletApi.getBalance();
      if (walletRes.status === 'success' && walletRes.data) {
        setWalletBalance(walletRes.data);
      }
    } catch (err) {
      console.error('Error refreshing wallet balance:', err);
    }
  };

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [customersRes, itemsRes, walletRes] = await Promise.all([
        customerApi.getAll(),
        stitchItemApi.getAll(),
        walletApi.getBalance()
      ]);
      
      if (customersRes.status === 'success' && customersRes.data) {
        setCustomers(customersRes.data);
      }
      
      if (itemsRes.status === 'success' && itemsRes.data) {
        setStitchItems(itemsRes.data);
      }

      if (walletRes.status === 'success' && walletRes.data) {
        setWalletBalance(walletRes.data);
      }
    } catch (err) {
      setError('Error loading data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadCustomers = async (params?: PaginationParams) => {
    try {
      setCustomerSearchLoading(true);
      // For order creation, we want to show all customers (including deleted ones)
      const response = await customerApi.getAllForOrders(params || customerSearchParams);
      if (response.status === 'success' && response.data) {
        setCustomers(response.data);
        if (response.pagination) {
          setCustomerPagination(response.pagination);
        }
      }
    } catch (err) {
      console.error('Load customers error:', err);
    } finally {
      setCustomerSearchLoading(false);
    }
  };

  const searchCustomers = async () => {
    const searchParams = {
      ...customerSearchParams,
      search: searchTerm,
      page: 1, // Reset to first page when searching
    };
    setCustomerSearchParams(searchParams);
    await loadCustomers(searchParams);
  };

  const handleCustomerPageChange = (page: number) => {
    const newParams = { ...customerSearchParams, page };
    setCustomerSearchParams(newParams);
    loadCustomers(newParams);
  };

  const handleNext = () => {
    if (activeStep === 0 && !formData.customer_id) {
      setError('Please select a customer first');
      return;
    }
    if (activeStep === 1 && formData.items.length === 0) {
      setError('Please add at least one item to the order');
      return;
    }
    
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    setError(null);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleCustomerSelect = (customer: Customer) => {
    setFormData(prev => ({ ...prev, customer_id: customer.id }));
    setSelectedCustomer(customer);
    setShowCustomerDialog(false);
  };

  const addOrderItem = async (item: StitchItem) => {
    const newItem: OrderItemFormData = {
      stitch_item_id: item.id,
      quantity: 1,
      unit_price: item.base_price.toString(),
      measurements: item.measurements?.map(measurement => ({
        measurement_name: measurement.measurement_name,
        measurement_value: '',
        unit: measurement.unit
      })) || [],
      special_instructions: '',
    };

    // Try to get previous measurements for this customer and stitch item
    if (formData.customer_id && formData.customer_id > 0) {
      try {
        const response = await orderApi.getPreviousMeasurements(formData.customer_id, item.id);
        if (response.status === 'success' && response.data?.measurements) {
          // Auto-fill measurements from previous order
          const previousMeasurements = response.data.measurements;
          newItem.measurements = item.measurements?.map(measurement => {
            const previousMeasurement = previousMeasurements.find((pm: any) => 
              pm.measurement_name === measurement.measurement_name
            );
            return {
              measurement_name: measurement.measurement_name,
              measurement_value: previousMeasurement?.measurement_value || '',
              unit: measurement.unit
            };
          }) || [];
          
          // Show a notification that measurements were auto-filled
          setSuccess(`Measurements auto-filled from previous order (${new Date(response.data.order_date).toLocaleDateString()})`);
          setTimeout(() => setSuccess(null), 5000);
        }
      } catch (error) {
        // Silently handle error - no previous measurements found
      }
    }
    
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));
    setShowItemDialog(false);
  };

  const updateOrderItem = (index: number, field: keyof OrderItemFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const removeOrderItem = (index: number) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const updateMeasurement = (itemIndex: number, measurementIndex: number, field: keyof MeasurementFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => 
        i === itemIndex ? {
          ...item,
          measurements: item.measurements.map((m, j) => 
            j === measurementIndex ? { ...m, [field]: value } : m
          )
        } : item
      )
    }));
  };

  const calculateItemTotal = (item: OrderItemFormData) => {
    const quantity = parseFloat(item.quantity.toString()) || 0;
    const unitPrice = parseFloat(item.unit_price) || 0;
    return (quantity * unitPrice).toFixed(2);
  };

  const calculateOrderTotal = () => {
    return formData.items.reduce((total, item) => {
      return total + parseFloat(calculateItemTotal(item));
    }, 0).toFixed(2);
  };

  const calculateBalance = () => {
    const total = parseFloat(calculateOrderTotal());
    const advance = parseFloat(formData.advance_amount) || 0;
    return Math.max(0, total - advance).toFixed(2);
  };

  const handleSubmit = async () => {
    if (!formData.customer_id || formData.items.length === 0) {
      setError('Please complete all required fields');
      return;
    }

    try {
      setSaving(true);
      // Create the order data with proper structure for the API
      const orderData: OrderApiData = {
        customer_id: formData.customer_id,
        order_date: formData.order_date,
        delivery_date: formData.delivery_date,
        total_amount: calculateOrderTotal(),
        advance_amount: formData.advance_amount,
        balance_amount: calculateBalance(),
        status: 'pending',
        notes: formData.notes,
        items: formData.items.map(item => ({
          stitch_item_id: item.stitch_item_id,
          quantity: item.quantity,
          unit_price: item.unit_price,
          total_price: calculateItemTotal(item),
          measurements_data: item.measurements,
          special_instructions: item.special_instructions || ''
        }))
      };

      const response = await orderApi.create(orderData);
      if (response.status === 'success') {
        // Refresh wallet balance after successful order creation
        await refreshWalletBalance();
        
        // Navigate to the newly created order details page
        if (response.id) {
          navigate(`/orders/${response.id}`);
        } else {
          // Fallback to orders list if no ID is returned
          navigate('/orders');
        }
      } else {
        setError(response.message || 'Failed to create order');
      }
    } catch (err) {
      setError('Error creating order');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const getCustomerName = (customerId: number) => {
    // First check if we have a selectedCustomer (from pre-selection)
    if (selectedCustomer && selectedCustomer.id === customerId) {
      return selectedCustomer.name;
    }
    // Then check the customers array
    const customer = customers.find(c => c.id === customerId);
    return customer ? customer.name : 'Select Customer';
  };

  const getItemName = (itemId: number) => {
    const item = stitchItems.find(i => i.id === itemId);
    return item ? item.name : 'Unknown Item';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Wallet Balance */}
      {walletBalance && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>Wallet Balance</Typography>
              <Typography variant="h6" fontWeight={700}>
                {walletBalance.balance} {walletBalance.currency}
              </Typography>
            </Box>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              Each order costs 5 credits
            </Typography>
          </Box>
        </Paper>
      )}

      <Paper sx={{ p: 3 }}>
        <Stepper activeStep={activeStep} orientation="vertical">
          {/* Step 1: Customer Selection */}
          <Step>
            <StepLabel>Customer Selection</StepLabel>
            <StepContent>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                <Box sx={{ flex: '1 1 400px', minWidth: '300px' }}>
                  <TextField
                    fullWidth
                    label="Customer"
                    value={getCustomerName(formData.customer_id)}
                    InputProps={{
                      readOnly: true,
                      startAdornment: <PersonIcon sx={{ mr: 1 }} />,
                    }}
                    onClick={() => setShowCustomerDialog(true)}
                    sx={{ cursor: 'pointer' }}
                    helperText={selectedCustomer ? "Click to change customer" : "Click to select customer"}
                  />
                  {selectedCustomer && (
                    <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip 
                        label="Customer Selected" 
                        color="success" 
                        size="small" 
                        icon={<PersonIcon />}
                      />
                      <Typography variant="caption" color="text.secondary">
                        You can change this by clicking above
                      </Typography>
                    </Box>
                  )}
                </Box>
                <Box sx={{ flex: '0 1 200px', mb: 2 }}>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => setShowCustomerDialog(true)}
                    fullWidth
                  >
                    {selectedCustomer ? 'Change Customer' : 'Select Customer'}
                  </Button>
                </Box>
              </Box>
              
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={!formData.customer_id}
                >
                  Next
                </Button>
              </Box>
            </StepContent>
          </Step>

          {/* Step 2: Order Items */}
          <Step>
            <StepLabel>Order Items</StepLabel>
            <StepContent>
              <Box sx={{ mb: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => setShowItemDialog(true)}
                >
                  Add Item
                </Button>
              </Box>

              {formData.items.map((item, index) => (
                <Card key={index} sx={{ mb: 2 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                      <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
                        <Typography variant="subtitle2" color="primary">
                          {getItemName(item.stitch_item_id)}
                        </Typography>
                      </Box>
                      <Box sx={{ flex: '1 1 200px', minWidth: '180px' }}>
                        <TextField
                          fullWidth
                          label="Quantity"
                          type="number"
                          value={item.quantity}
                          onChange={(e) => updateOrderItem(index, 'quantity', parseInt(e.target.value) || 1)}
                          inputProps={{ min: 1 }}
                        />
                      </Box>
                      <Box sx={{ flex: '1 1 200px', minWidth: '180px' }}>
                        <TextField
                          fullWidth
                          label="Unit Price"
                          type="number"
                          value={item.unit_price}
                          onChange={(e) => updateOrderItem(index, 'unit_price', e.target.value)}
                          InputProps={{
                            startAdornment: <InputAdornment position="start">{APP_CONFIG.CURRENCY}</InputAdornment>,
                          }}
                        />
                      </Box>
                      <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
                        <Typography variant="subtitle2">
                          Total: {APP_CONFIG.CURRENCY}{calculateItemTotal(item)}
                        </Typography>
                      </Box>
                      <Box sx={{ flex: '0 1 100px' }}>
                        <IconButton
                          color="error"
                          onClick={() => removeOrderItem(index)}
                        >
                          <RemoveIcon />
                        </IconButton>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}

              <Box sx={{ mt: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleBack}
                  sx={{ mr: 1 }}
                >
                  Back
                </Button>
                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={formData.items.length === 0}
                >
                  Next
                </Button>
              </Box>
            </StepContent>
          </Step>

          {/* Step 3: Measurements & Pricing */}
          <Step>
            <StepLabel>Measurements & Pricing</StepLabel>
            <StepContent>
              {formData.items.map((item, itemIndex) => (
                <Card key={itemIndex} sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {getItemName(item.stitch_item_id)} - Measurements
                    </Typography>
                    
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                      {item.measurements.map((measurement, measurementIndex) => (
                        <Box sx={{ flex: '1 1 300px', minWidth: '250px' }} key={measurementIndex}>
                          <TextField
                            fullWidth
                            label={measurement.measurement_name}
                            value={measurement.measurement_value}
                            onChange={(e) => updateMeasurement(itemIndex, measurementIndex, 'measurement_value', e.target.value)}
                            InputProps={{
                              endAdornment: <InputAdornment position="end">{measurement.unit}</InputAdornment>,
                            }}
                          />
                        </Box>
                      ))}
                    </Box>
                    
                    <TextField
                      fullWidth
                      label="Special Instructions"
                      value={item.special_instructions}
                      onChange={(e) => updateOrderItem(itemIndex, 'special_instructions', e.target.value)}
                      multiline
                      rows={2}
                      sx={{ mt: 2 }}
                    />
                  </CardContent>
                </Card>
              ))}

              <Box sx={{ mt: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleBack}
                  sx={{ mr: 1 }}
                >
                  Back
                </Button>
                <Button
                  variant="contained"
                  onClick={handleNext}
                >
                  Next
                </Button>
              </Box>
            </StepContent>
          </Step>

          {/* Step 4: Review & Submit */}
          <Step>
            <StepLabel>Review & Submit</StepLabel>
            <StepContent>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                <Box sx={{ flex: '1 1 400px', minWidth: '350px' }}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Order Summary
                      </Typography>
                      <Typography><strong>Customer:</strong> {getCustomerName(formData.customer_id)}</Typography>
                      <Typography><strong>Items:</strong> {formData.items.length}</Typography>
                      <Typography><strong>Total Amount:</strong> {APP_CONFIG.CURRENCY}{calculateOrderTotal()}</Typography>
                    </CardContent>
                  </Card>
                </Box>
                
                <Box sx={{ flex: '1 1 400px', minWidth: '350px' }}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Payment Details
                      </Typography>
                      <TextField
                        fullWidth
                        label="Advance Amount"
                        type="number"
                        value={formData.advance_amount}
                        onChange={(e) => setFormData(prev => ({ ...prev, advance_amount: e.target.value }))}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">{APP_CONFIG.CURRENCY}</InputAdornment>,
                        }}
                        sx={{ mb: 2 }}
                      />
                      <Typography><strong>Balance Amount:</strong> {APP_CONFIG.CURRENCY}{calculateBalance()}</Typography>
                    </CardContent>
                  </Card>
                </Box>
                
                <Box sx={{ flex: '1 1 100%' }}>
                  <TextField
                    fullWidth
                    label="Delivery Date"
                    type="date"
                    value={formData.delivery_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, delivery_date: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                    sx={{ mb: 2 }}
                  />
                  
                  <TextField
                    fullWidth
                    label="Notes"
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    multiline
                    rows={3}
                  />
                </Box>
              </Box>

              <Box sx={{ mt: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleBack}
                  sx={{ mr: 1 }}
                >
                  Back
                </Button>
                <Button
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                  onClick={handleSubmit}
                  disabled={saving}
                >
                  {saving ? 'Creating Order...' : 'Create Order'}
                </Button>
              </Box>
            </StepContent>
          </Step>
        </Stepper>
      </Paper>

      {/* Customer Selection Dialog */}
      <Dialog 
        open={showCustomerDialog} 
        onClose={() => setShowCustomerDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Select Customer</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', gap: 1, mb: 2, mt: 1 }}>
            <TextField
              fullWidth
              placeholder="Search customers by name or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  searchCustomers();
                }
              }}
              size="small"
            />
            <Button
              variant="contained"
              onClick={searchCustomers}
              disabled={customerSearchLoading}
              size="small"
            >
              Search
            </Button>
            {searchTerm && (
              <Button
                variant="outlined"
                onClick={() => {
                  setSearchTerm('');
                  setCustomerSearchParams({
                    page: 1,
                    limit: 10,
                    search: '',
                    sort_by: 'name',
                    sort_order: 'ASC'
                  });
                  loadCustomers();
                }}
                size="small"
              >
                Clear
              </Button>
            )}
          </Box>
          
          {customerSearchLoading ? (
            <Box display="flex" justifyContent="center" p={2}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <>
              <List>
                {customers.map(customer => (
                  <ListItem 
                    key={customer.id}
                    onClick={() => handleCustomerSelect(customer)}
                    sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                  >
                    <ListItemText
                      primary={customer.name}
                      secondary={`${customer.phone}${customer.email ? ` • ${customer.email}` : ''}`}
                    />
                  </ListItem>
                ))}
              </List>
              
              {customerPagination.total_pages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={() => handleCustomerPageChange(customerPagination.current_page - 1)}
                    disabled={!customerPagination.has_prev}
                    size="small"
                    sx={{ mr: 1 }}
                  >
                    Previous
                  </Button>
                  <Typography variant="body2" sx={{ alignSelf: 'center', mx: 2 }}>
                    Page {customerPagination.current_page} of {customerPagination.total_pages}
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={() => handleCustomerPageChange(customerPagination.current_page + 1)}
                    disabled={!customerPagination.has_next}
                    size="small"
                  >
                    Next
                  </Button>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCustomerDialog(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Item Selection Dialog */}
      <Dialog 
        open={showItemDialog} 
        onClose={() => setShowItemDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Select Stitch Item</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {stitchItems.map(item => (
              <Box sx={{ flex: '1 1 400px', minWidth: '350px' }} key={item.id}>
                <Card>
                  <CardContent>
                    <Typography variant="h6">{item.name}</Typography>
                    <Typography variant="body2" color="textSecondary">
                      {item.description}
                    </Typography>
                    <Typography variant="subtitle1" color="primary" sx={{ mt: 1 }}>
                      Base Price: {APP_CONFIG.CURRENCY}{item.base_price}
                    </Typography>
                  </CardContent>
                  <CardActions>
                    <Button
                      variant="contained"
                      onClick={() => addOrderItem(item)}
                      fullWidth
                    >
                      Add to Order
                    </Button>
                  </CardActions>
                </Card>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowItemDialog(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NewOrder; 